/** Section 3.3 - the single legality source for the adventure kernel.
 *
 * Contract: every *concrete* command returned executes `Ok` against the state
 * it was generated from. `MOVE_HERO` is the one parameterised family - one
 * Dijkstra pass per hero, handed back as a `ReachableSet` rather than as tens
 * of thousands of paths - so the caller picks a destination and builds the
 * command with `pathTo`.
 *
 * While `state.pending` is not null this returns nothing but
 * `RESPOND_TO_INTERACTION`, and only for the player the prompt is addressed to.
 */

import type { GameCommand } from "../commands.js";
import type { ContentRegistry } from "../content/registry.js";
import type {
  ArmyId, ArtifactDefId, BuildingDefId, CreatureDefId, HeroId, ObjectId, PlayerId, TownId,
} from "../ids.js";
import type { GameState, ResourceKind, TownState } from "../state.js";
import { RESOURCES } from "../state.js";
import { entityKeys } from "../util/iter.js";
import { computeReachable, type ReachableSet } from "../map/path.js";
import { indexOf } from "../map/create.js";
import { SIDE_ATTACKER, SIDE_DEFENDER } from "../battle/state.js";
import { enumerateLegalBattleCommands } from "../battle/legal.js";
import { makeCtx, missingResource, type Ctx } from "./ctx.js";
import { MAX_HEROES_PER_PLAYER, hasFreeSlotFor, moveRulesFor } from "./hero.js";
import { freeSlotFor } from "./slots.js";
import { enumerableTargets } from "./spells.js";
import {
  availableOf, buildIllegality, buildingsOfFaction, guildLevelOf, recruitCost, recruitable,
  townUpgradeOffers, upgradableArmies,
} from "./town.js";
import { researchCandidates, researchIllegality } from "./research.js";
import { objectSource, townSources, validateCaravan } from "./caravan.js";
import { payloadOf, payNum, payStr } from "./objects.js";
import {
  HERO_HIRE_COST, digIllegality, heroesMeeting, townBuyIllegality, townShopStock, tradeYield,
} from "./execute.js";
import { assemblableFor, disassembleIllegality } from "./assemble.js";

/** The `MOVE_HERO` family: one entry per hero, one Dijkstra pass behind it. */
export interface MoveHeroFamily {
  readonly type: "MOVE_HERO";
  readonly heroId: HeroId;
  readonly reachable: ReachableSet;
}

export type LegalCommand = GameCommand | MoveHeroFamily;

export const isMoveFamily = (c: LegalCommand): c is MoveHeroFamily =>
  c.type === "MOVE_HERO" && "heroId" in c;

export interface LegalCommandFilter {
  readonly kinds?: readonly GameCommand["type"][];
  readonly limit?: number;
}

export function enumerateLegalCommands(
  state: GameState, playerId: PlayerId, content: ContentRegistry, filter?: LegalCommandFilter,
): readonly LegalCommand[] {
  const out: LegalCommand[] = [];
  const limit = filter?.limit ?? Number.MAX_SAFE_INTEGER;
  const kinds = filter?.kinds;
  const push = (cmd: LegalCommand): void => {
    if (out.length >= limit) return;
    if (kinds !== undefined && !kinds.includes(cmd.type)) return;
    out.push(cmd);
  };

  if (state.victory.over) return out;

  if (state.pending !== null) {
    if (state.pending.playerId !== playerId) return out;
    for (const r of state.pending.legalResponses) {
      push({ type: "RESPOND_TO_INTERACTION", id: state.pending.id, response: r });
    }
    return out;
  }

  if (state.battle !== null) {
    const side = battleSideOf(state, playerId);
    if (side === null) return out;
    for (const cmd of enumerateLegalBattleCommands(state.battle, side, content)) {
      push({ type: "BATTLE", cmd });
    }
    return out;
  }

  if (state.turn.activePlayer !== playerId) return out;
  const p = state.players[playerId];
  if (p === undefined || p.defeated) return out;

  const c = makeCtx(state, content);

  push({ type: "END_TURN", player: playerId });
  heroCommands(c, playerId, push);
  townCommands(c, playerId, push);
  armyCommands(c, playerId, push);
  marketCommands(c, playerId, push);
  return out;
}

/**
 * Battle commands for the side no player controls.
 *
 * A wandering monster has no `PlayerId`, so nothing reaches it through
 * `enumerateLegalCommands`. Until a neutral controller exists, this is the door
 * the harness and the AI drive it through.
 */
export function enumerateNeutralBattleCommands(
  state: GameState, content: ContentRegistry,
): readonly GameCommand[] {
  const b = state.battle;
  if (b === null) return [];
  const out: GameCommand[] = [];
  for (const side of [SIDE_ATTACKER, SIDE_DEFENDER] as const) {
    if (b.heroes[side].owner !== null) continue;
    for (const cmd of enumerateLegalBattleCommands(b, side, content)) {
      out.push({ type: "BATTLE", cmd });
    }
  }
  return out;
}

function battleSideOf(state: GameState, p: PlayerId): 0 | 1 | null {
  const b = state.battle;
  if (b === null) return null;
  if (b.heroes[SIDE_ATTACKER].owner === p) return SIDE_ATTACKER as 0;
  if (b.heroes[SIDE_DEFENDER].owner === p) return SIDE_DEFENDER as 1;
  return null;
}

// ------------------------------------------------------------------ heroes

type Push = (cmd: LegalCommand) => void;

function heroCommands(c: Ctx, playerId: PlayerId, push: Push): void {
  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id]!;
    if (h.owner !== playerId) continue;
    const heroId = id as HeroId;

    if (h.movementLeft > 0) {
      const origin = indexOf(c.s.map, h.x, h.y, h.z);
      if (origin >= 0) {
        push({
          type: "MOVE_HERO", heroId,
          reachable: computeReachable(c.s.map, origin, moveRulesFor(c, h)),
        });
      }
    }

    push({ type: "DISMISS_HERO", hero: heroId });

    // Section 39: a dig is a whole day, so it is only on offer to a hero who
    // still has one. `digIllegality` is the same gate the executor runs.
    if (digIllegality(c, h) === null) push({ type: "DIG", hero: heroId });

    for (let i = 0; i < h.backpack.length; i++) {
      const art = h.backpack[i]!;
      const def = c.content.artifacts.get(art);
      if (def === undefined) continue;
      // The first free socket of the artifact's family: a second trinket goes
      // to `misc2`, not nowhere.
      const slot = freeSlotFor(h, def);
      if (slot === null) continue;
      push({ type: "EQUIP_ARTIFACT", hero: heroId, backpackIndex: i, slot });
    }
    for (const slot of Object.keys(h.artifacts).sort()) {
      if (h.artifacts[slot] == null) continue;
      push({ type: "UNEQUIP_ARTIFACT", hero: heroId, slot });
      // A combination artifact is the only thing that answers yes here, so this
      // is one map lookup on a slot that almost never holds one.
      if (disassembleIllegality(c.content, h, slot) === null) {
        push({ type: "DISASSEMBLE_ARTIFACT", hero: heroId, slot });
      }
    }
    // Handing a bagged artifact to the hero standing beside you. Enumerated
    // per (bag slot x neighbouring hero), which is bounded by the bag rather
    // than by the roster: a player rarely has two heroes touching, and when he
    // does the second loop runs once.
    for (const otherId of entityKeys(c.s.heroes)) {
      const other = c.s.heroes[otherId]!;
      if (other.id === heroId || !heroesMeeting(h, other)) continue;
      for (let i = 0; i < h.backpack.length; i++) {
        push({ type: "GIVE_ARTIFACT", fromHero: heroId, toHero: otherId as HeroId, backpackIndex: i });
      }
    }

    // `assemblableFor` walks the artifact table, so it is asked once per hero
    // rather than once per slot. Fifteen of a hundred and fifty-nine artifacts
    // have components and the rest are rejected on their first line.
    for (const art of assemblableFor(c.content, h)) {
      push({ type: "ASSEMBLE_ARTIFACT", hero: heroId, artifact: art });
    }

    for (const tid of entityKeys(c.s.towns)) {
      if (h.boat !== null) break;
      const t = c.s.towns[tid]!;
      if (t.owner !== playerId) continue;
      if (t.visitingHero !== null && t.visitingHero !== heroId) continue;
      if (t.z !== h.z || Math.abs(t.x - h.x) > 1 || Math.abs(t.y - h.y) > 1) continue;
      push({ type: "HERO_TO_TOWN", hero: heroId, town: tid as TownId });
    }

    // Section 27: the Blacksmith's counter, and the guild's spellbook. Only a
    // hero standing in the town can buy, so this is asked of `inTown` rather
    // than of every town the player owns.
    if (h.inTown !== null) {
      const shop = c.s.towns[h.inTown];
      if (shop !== undefined) {
        for (const art of townShopStock(c, shop)) {
          if (townBuyIllegality(c, h, shop, art) !== null) continue;
          push({ type: "TOWN_BUY_ARTIFACT", hero: heroId, town: shop.id, artifact: art });
        }
      }
    }

    for (const oid of entityKeys(c.s.objects)) {
      const o = c.s.objects[oid]!;
      if (o.removed) continue;
      if (o.z !== h.z || Math.abs(o.x - h.x) > 1 || Math.abs(o.y - h.y) > 1) continue;
      const def = c.content.mapObjects.get(o.def);
      if (def?.oneVisitPerHero === true && h.visited.includes(oid as ObjectId)) continue;
      // A guarded object opens a fight, which is legal; an obstacle is not.
      if (o.kind === "obstacle") continue;
      // A town is walked into, not reached into. `objects.ts` refuses a visit
      // from the next cell over - the capture, the siege and the garrison fold
      // all live on the movement loop - so offering one from there would break
      // the contract that everything enumerated executes Ok. `HERO_TO_TOWN` is
      // the adjacent gesture and it is pushed above.
      const standingOn = o.x === h.x && o.y === h.y && o.z === h.z;
      if (o.kind === "town" && !standingOn) continue;
      push({ type: "VISIT_OBJECT", hero: heroId, object: oid as ObjectId });

      const pay = payloadOf(c, o);
      const art = payStr(pay, "artifact");
      if (art !== "" && o.kind !== "artifact") {
        const adef = c.content.artifacts.get(art as ArtifactDefId);
        if (adef !== undefined) {
          const price = payNum(pay, "price", adef.cost);
          const pl = c.s.players[playerId]!;
          if (pl.resources.gold >= price) {
            push({
              type: "BUY_ARTIFACT", hero: heroId,
              object: oid as ObjectId, artifact: art as ArtifactDefId,
            });
          }
        }
      }
    }

    spellCommands(c, heroId, push);
  }
}

/**
 * Adventure casts, one command per target `spells.ts` says it can name. The
 * split between "the module knows the targets" and "the caller picks the cell"
 * is the same one `MOVE_HERO` uses, and it is what keeps this enumerator's
 * contract - every concrete command it returns executes `Ok` - provable.
 */
function spellCommands(c: Ctx, heroId: HeroId, push: Push): void {
  const h = c.s.heroes[heroId]!;
  for (const spell of h.spellbook) {
    for (const [x, y, z] of enumerableTargets(c, heroId, spell)) {
      push({ type: "CAST_ADVENTURE_SPELL", hero: heroId, spell, x, y, z });
    }
  }
}

// ------------------------------------------------------------------- towns

function townCommands(c: Ctx, playerId: PlayerId, push: Push): void {
  const p = c.s.players[playerId]!;
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.owner !== playerId) continue;
    const townId = id as TownId;

    for (const b of buildingsOfFaction(c.content, t.faction as string)) {
      if (buildIllegality(c, t, b.id as BuildingDefId) !== null) continue;
      push({ type: "BUILD", town: townId, building: b.id as BuildingDefId });
    }
    push({ type: "TOGGLE_AUTO_BUILD", town: townId });

    // The expansion spell research. Both of the day's candidates are enumerated, so the
    // choice between them is made by picking a command rather than by
    // answering a prompt - and the AI weighs them like any other option.
    for (let lvl = 1; lvl <= guildLevelOf(c, t); lvl++) {
      const offers = researchCandidates(c, t, lvl);
      for (let pick = 0; pick < offers.length; pick++) {
        if (researchIllegality(c, townId, lvl, pick) !== null) continue;
        push({ type: "RESEARCH_SPELL", town: townId, level: lvl, pick });
      }
    }

    const target = recruitTarget(c, t);
    const army = target === null ? null : c.s.armies[target] ?? null;
    for (const creature of recruitable(c, t)) {
      const have = availableOf(c, t, creature);
      if (have <= 0 || army === null) continue;
      if (!hasFreeSlotFor(army, creature)) continue;
      const most = affordableCount(c, playerId, creature, have);
      if (most <= 0) continue;
      push({ type: "RECRUIT", town: townId, creature, count: 1 });
      if (most > 1) push({ type: "RECRUIT", town: townId, creature, count: most });
    }

    // Upgrades for every stack the town can reach, garrison and visiting hero
    // alike. Priced per stack, so affordability is checked against the offer
    // rather than recomputed here.
    for (const armyId of upgradableArmies(c, t)) {
      for (const offer of townUpgradeOffers(c, t, armyId)) {
        if (missingResource(p.resources, offer.cost) !== null) continue;
        push({ type: "UPGRADE_UNITS", town: townId, army: armyId, slot: offer.slot });
      }
    }

    if (t.visitingHero === null && p.heroes.length < MAX_HEROES_PER_PLAYER
        && p.resources.gold >= HERO_HIRE_COST && hasTavern(c, t)) {
      for (const def of p.tavern) push({ type: "HIRE_HERO", town: townId, hero: def });
    }
  }
  caravanCommands(c, playerId, push);
}

/**
 * Every column this player could send right now.
 *
 * Enumerated at two counts per (source, destination, creature) - one, and as
 * many as the source has and the purse can carry - which is the same pair
 * `RECRUIT` offers and for the same reason: those are the two a player actually
 * picks, and enumerating every count in between would put thousands of
 * near-identical commands in front of the AI's scorer.
 *
 * Every candidate goes through `validateCaravan`, the same function the
 * executor runs, so the legality contract holds by construction rather than by
 * two lists agreeing.
 */
function caravanCommands(c: Ctx, playerId: PlayerId, push: Push): void {
  const towns: TownId[] = [];
  for (const id of entityKeys(c.s.towns)) {
    if (c.s.towns[id]!.owner === playerId) towns.push(id as TownId);
  }
  if (towns.length === 0) return;

  const offer = (
    to: TownId, fromTown: TownId | null, fromObject: ObjectId | null,
    creature: CreatureDefId, stock: number,
  ): void => {
    if (validateCaravan(c, to, fromTown, fromObject, creature, 1).ok) {
      push({ type: "SEND_CARAVAN", town: to, fromTown, fromObject, creature, count: 1 });
    }
    const most = affordableCount(c, playerId, creature, stock);
    if (most > 1 && validateCaravan(c, to, fromTown, fromObject, creature, most).ok) {
      push({ type: "SEND_CARAVAN", town: to, fromTown, fromObject, creature, count: most });
    }
  };

  // Owned external dwellings: the case the whole feature exists for. Their
  // stock accumulates week on week and, without a caravan, only a hero standing
  // on the tile can ever spend it.
  for (const id of entityKeys(c.s.objects)) {
    const src = objectSource(c, id as ObjectId, playerId);
    if (src === null) continue;
    for (const to of towns) offer(to, null, id as ObjectId, src.creature, src.stock);
  }

  // Town to town: a second hall's growth reaching the front without a hero
  // walking back for it.
  for (const from of towns) {
    const t = c.s.towns[from]!;
    for (const src of townSources(c, t)) {
      for (const to of towns) {
        if (to === from) continue;
        offer(to, from, null, src.creature, src.stock);
      }
    }
  }
}

function hasTavern(c: Ctx, t: TownState): boolean {
  for (const id of t.built) {
    const b = c.content.buildings.get(id);
    if (b !== undefined && b.slot === "tavern") return true;
  }
  return false;
}

function recruitTarget(c: Ctx, t: TownState): ArmyId | null {
  if (t.visitingHero !== null) return c.s.heroes[t.visitingHero]?.army ?? null;
  if (t.garrisonHero !== null) return c.s.heroes[t.garrisonHero]?.army ?? null;
  return t.garrison;
}

function affordableCount(
  c: Ctx, playerId: PlayerId, creature: CreatureDefId, cap: number,
): number {
  const p = c.s.players[playerId]!;
  let n = cap;
  const unit = recruitCost(c.content, creature, 1);
  for (const r of RESOURCES) {
    const per = unit[r] ?? 0;
    if (per <= 0) continue;
    const can = Math.floor(p.resources[r] / per);
    if (can < n) n = can;
  }
  return n < 0 ? 0 : n;
}

// -------------------------------------------------------------------- army

function armyCommands(c: Ctx, playerId: PlayerId, push: Push): void {
  // Only armies somebody HOLDS: a hero's, or a town's garrison. An army a
  // surrendered hero left for his re-hire is the player's but nobody's to
  // rearrange, and enumerating it offered thirty-odd commands on a ghost.
  const held = new Set<string>();
  for (const hid of entityKeys(c.s.heroes)) held.add(c.s.heroes[hid]!.army as string);
  for (const tid of entityKeys(c.s.towns)) held.add(c.s.towns[tid]!.garrison as string);
  const armies: ArmyId[] = [];
  for (const id of entityKeys(c.s.armies)) {
    if (c.s.armies[id]!.owner === playerId && held.has(id as string)) armies.push(id as ArmyId);
  }

  for (const id of armies) {
    const a = c.s.armies[id]!;
    const stacks = a.slots.filter((s) => s !== null && s.count > 0).length;
    const heroOwned = ownedByHero(c, id);
    for (let i = 0; i < a.slots.length; i++) {
      const s = a.slots[i];
      if (s === null || s === undefined) continue;
      for (let j = 0; j < a.slots.length; j++) {
        if (i === j) continue;
        const d = a.slots[j];
        if (d === null || d === undefined) {
          if (s.count > 1) push({ type: "SPLIT_STACK", army: id, from: i, to: j, count: 1 });
          if (s.count > 2) {
            push({ type: "SPLIT_STACK", army: id, from: i, to: j, count: s.count >> 1 });
          }
          push({ type: "SWAP_STACK", army: id, a: i, b: j });
        } else if (d.def === s.def) {
          push({ type: "MERGE_STACK", army: id, from: i, to: j });
        } else {
          push({ type: "SWAP_STACK", army: id, a: i, b: j });
        }
      }
      if (!(heroOwned && stacks === 1)) {
        push({ type: "DISMISS_STACK", army: id, slot: i });
      }
    }
  }

  for (const from of armies) {
    for (const to of armies) {
      if (from === to || !interactable(c, from, to)) continue;
      const a = c.s.armies[from]!;
      const b = c.s.armies[to]!;
      const stacks = a.slots.filter((s) => s !== null && s.count > 0).length;
      const heroOwned = ownedByHero(c, from);
      for (let i = 0; i < a.slots.length; i++) {
        const s = a.slots[i];
        if (s === null || s === undefined) continue;
        for (let j = 0; j < b.slots.length; j++) {
          const d = b.slots[j];
          if (d !== null && d !== undefined && d.def !== s.def) continue;
          const whole = s.count;
          if (heroOwned && stacks === 1) {
            if (whole > 1) {
              push({ type: "TRANSFER_STACK", fromArmy: from, fromSlot: i, toArmy: to, toSlot: j, count: whole - 1 });
            }
          } else {
            push({ type: "TRANSFER_STACK", fromArmy: from, fromSlot: i, toArmy: to, toSlot: j, count: whole });
          }
        }
      }
    }
  }
}

function ownedByHero(c: Ctx, id: ArmyId): boolean {
  for (const hid of entityKeys(c.s.heroes)) if (c.s.heroes[hid]!.army === id) return true;
  return false;
}

function positionOf(c: Ctx, id: ArmyId): readonly [number, number, number] | null {
  for (const hid of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[hid]!;
    if (h.army === id) return [h.x, h.y, h.z];
  }
  for (const tid of entityKeys(c.s.towns)) {
    const t = c.s.towns[tid]!;
    if (t.garrison === id) return [t.x, t.y, t.z];
  }
  for (const oid of entityKeys(c.s.objects)) {
    const o = c.s.objects[oid]!;
    if (o.guard === id) return [o.x, o.y, o.z];
  }
  return null;
}

function interactable(c: Ctx, a: ArmyId, b: ArmyId): boolean {
  const pa = positionOf(c, a), pb = positionOf(c, b);
  if (pa === null || pb === null) return false;
  return pa[2] === pb[2] && Math.abs(pa[0] - pb[0]) <= 1 && Math.abs(pa[1] - pb[1]) <= 1;
}

// ------------------------------------------------------------------ market

function marketCommands(c: Ctx, playerId: PlayerId, push: Push): void {
  const p = c.s.players[playerId]!;
  let markets = 0;
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.owner !== playerId) continue;
    for (const b of t.built) {
      if (c.content.buildings.get(b)?.slot === "marketplace") { markets++; break; }
    }
  }
  if (markets === 0) return;
  for (const give of RESOURCES) {
    if (p.resources[give] <= 0) continue;
    for (const take of RESOURCES) {
      if (take === give) continue;
      const amount = tradeAmountFor(c, playerId, give, take, p.resources[give]);
      if (amount > 0) push({ type: "TRADE_RESOURCE", player: playerId, give, take, amount });
    }
  }
}

/** The smallest amount of `give` that actually buys one unit of `take`. */
function tradeAmountFor(
  c: Ctx, p: PlayerId, give: ResourceKind, take: ResourceKind, have: number,
): number {
  for (let n = 1; n <= have; n++) {
    if (tradeYield(c, p, give, take, n) > 0) return n;
    if (n > 1000) break;
  }
  return 0;
}
